/**
 * JobFormTemplate - Composition layer for the work order / inspection
 * create + edit experience.
 *
 * Atomic Design role: Template. The template defines the SHAPE of the
 * form — the order of section cards, the global error banner, the
 * submission buttons — but owns no state or business logic. It simply
 * receives a bundle of props per section molecule (each is a Molecule
 * built from atoms via the existing CompanyLocationFieldset /
 * ContactFieldset / AssigneeFieldset / EstimateFieldset primitives) and
 * renders them in the canonical order.
 *
 * The same template renders both formType variants. The formType prop
 * is propagated to every section molecule so each can adapt its label
 * copy or hide internals where appropriate. The organism (JobForm) owns
 * all state, side effects, and the submission handler — making the
 * template a pure presentational layer.
 *
 * Section order:
 *	 1. ProjectInformationSection	  — shared (project name/description gated to work_order)
 *	 2. CustomerLocationSection		  — shared (Customer & Location/Building)
 *	 3. OnSiteContactSection		  — shared (3-mode contact picker)
 *	 4. AssignmentSection			  — shared (assignees + priority + date/times)
 *	 5. InspectionNotesSection		  — inspection only
 *	 6. BillingSection				  — shared
 *	 7. SettingsSection				  — shared (photos/signature/frequency/checklist/estimate)
 *	 8. Submit / Cancel button row
 */
import React, { useEffect, useRef } from 'react';
import { Spinner } from '../../atoms/Spinner';
import {
	ProjectInformationSection,
	CustomerLocationSection,
	OnSiteContactSection,
	AssignmentSection,
	InspectionNotesSection,
	BillingSection,
	SettingsSection,
} from '../../molecules';
import type {
	ProjectInformationSectionProps,
	CustomerLocationSectionProps,
	OnSiteContactSectionProps,
	AssignmentSectionProps,
	InspectionNotesSectionProps,
	BillingSectionProps,
	SettingsSectionProps,
} from '../../molecules';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** One entry in the submit-error summary list. */
export interface JobFormErrorDetail {
	/** DOM id of the offending field — the entry scrolls to + focuses it. */
	id: string;
	/** Human-readable message, e.g. "Project name is required". */
	message: string;
}

export interface JobFormTemplateProps {
	/** 'work_order' or 'inspection' — drives section gating + button copy. */
	formType: 'work_order' | 'inspection';
	/** Whether the form is currently submitting (disables the submit button). */
	isSubmitting: boolean;
	/** Whether the form is in edit mode — controls submit button label. */
	isEditMode: boolean;
	/** Top-of-form error banner copy; null hides the banner. */
	submitError: string | null;
	/** Per-field errors listed inside the banner. Each entry renders as a
	 *  link that scrolls to and focuses the offending field. */
	submitErrorDetails?: JobFormErrorDetail[];
	/** Bumped by the organism on every failed submit so the banner
	 *  re-runs its scroll/focus effect even when the copy is unchanged
	 *  between attempts. */
	submitErrorNonce?: number;
	/** Submission handler — wired up to the <form>'s onSubmit. */
	onSubmit: (e: React.FormEvent) => void;
	/**
	 * Change handler on the root <form>. React bubbles every input, select,
	 * checkbox and textarea change up to here, which is how the composing
	 * organism learns the user has started editing — for its unsaved-changes
	 * prompt — without every field reporting separately.
	 */
	onChange?: React.FormEventHandler<HTMLFormElement>;

	/** Project Information section props — required when formType is 'work_order'. */
	projectInformation?: ProjectInformationSectionProps;
	/** Customer & Location / Building section props (shared). */
	customerLocation: CustomerLocationSectionProps;
	/** Customer Contact section props (shared). */
	onSiteContact: OnSiteContactSectionProps;
	/** Assignment section props (shared). */
	assignment: AssignmentSectionProps;
	/** Inspection Notes section props — required when formType is 'inspection'. */
	inspectionNotes?: InspectionNotesSectionProps;
	/** Billing section props (shared). Optional so callers can omit the
	 *  section entirely — used by the child-WO create flow under a
	 *  Project Work Order, where billing is captured at the project
	 *  level instead. */
	billing?: BillingSectionProps;
	/** Settings section props (shared). */
	settings: SettingsSectionProps;
	/** Optional extra classes merged onto the root <form> element. */
	className?: string;
	/**
	 * Overrides the data-ref marker so a composing organism (e.g. JobForm)
	 * keeps its own atomic identity. Defaults to the template marker.
	 */
	dataRef?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const JobFormTemplate: React.FC<JobFormTemplateProps> = ({
	formType,
	isSubmitting,
	isEditMode,
	submitError,
	submitErrorDetails,
	submitErrorNonce,
	onSubmit,
	onChange,
	projectInformation,
	customerLocation,
	onSiteContact,
	assignment,
	inspectionNotes,
	billing,
	settings,
	className,
	dataRef = 'template-job-form-template',
}) => {
	/* The submit button copy varies across both edit-mode and form-type
	   axes. Resolve once here so the JSX below stays readable. */
	const submitLabel = isEditMode
		? (formType === 'work_order' ? 'Save Work Order' : 'Save Inspection')
		: (formType === 'work_order' ? 'Create Work Order' : 'Create Inspection');

	const submittingLabel = isEditMode ? 'Saving...' : 'Creating...';

	/* ---- Submit-error summary focus management ----
	   Without this, tapping "Create Work Order" at the bottom of a long
	   form (especially on mobile) appears to do nothing: the feedback
	   renders far off-screen at the top. On every failed submit we move
	   focus to the summary (so screen readers announce it) and scroll it
	   into view — the GOV.UK error-summary pattern. The nonce dep
	   re-triggers the effect when a second attempt fails with the same
	   message. */
	const errorSummaryRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!submitError) return;
		const summary = errorSummaryRef.current;
		if (!summary) return;
		/* Announce immediately... */
		summary.focus({ preventScroll: true });
		/* ...but scroll two frames later. The summary + inline field
		   errors mount in this same commit, and Chrome's scroll-anchoring
		   pass (which compensates for content inserted above the
		   viewport) lands at the NEXT layout — a scroll issued now, or in
		   a single rAF, is silently overwritten by that adjustment and
		   the user stays pinned at the bottom of the form. Instant (not
		   smooth) so nothing can cancel it mid-flight. The timeout is a
		   fallback for contexts where rAF is frozen (hidden/occluded
		   tabs); both fire the same idempotent scroll. */
		const scrollToSummary = () => summary.scrollIntoView({ block: 'start' });
		let raf2 = 0;
		const raf1 = requestAnimationFrame(() => {
			raf2 = requestAnimationFrame(scrollToSummary);
		});
		const fallback = window.setTimeout(scrollToSummary, 80);
		return () => {
			cancelAnimationFrame(raf1);
			cancelAnimationFrame(raf2);
			window.clearTimeout(fallback);
		};
	}, [submitError, submitErrorNonce]);

	/* Jump target for a summary entry. block:'center' keeps the field
	   clear of the sticky TopBar and works inside modal scroll bodies;
	   focusing lets the user correct the field immediately (and pops
	   open the searchable-select dropdowns). */
	const focusErrorField = (fieldId: string) => {
		const field = document.getElementById(fieldId);
		if (!field) return;
		/* Scroll BEFORE focusing: focus has layout side-effects (the
		   searchable selects open their dropdown on focus; mobile
		   keyboards resize the viewport) that would invalidate a scroll
		   computed afterwards. */
		field.scrollIntoView({ block: 'center' });
		field.focus({ preventScroll: true });
	};

	return (
		<>
			{/* Global submit error summary — only renders when set. Focusable
			    (tabIndex -1) so the effect above can land keyboard/screen-
			    reader users on it; scroll-mt clears the 64px sticky TopBar. */}
			{submitError && (
				<div
					ref={errorSummaryRef}
					tabIndex={-1}
					role="alert"
					data-ref="template-job-form-error-summary"
					className="job-form-template__error-summary mb-16px px-16px py-12px rounded-md text-14px bg-red-50 text-red-800 border border-red-200 scroll-mt-80px focus:outline-none"
				>
					<p className="job-form-template__error-summary-title font-medium">{submitError}</p>
					{submitErrorDetails && submitErrorDetails.length > 0 && (
						<ul className="job-form-template__error-summary-list mt-8px space-y-4px pl-20px list-disc">
							{submitErrorDetails.map(({ id, message }) => (
								<li key={id} className="job-form-template__error-summary-item">
									<a
										href={`#${id}`}
										onClick={(e) => { e.preventDefault(); focusErrorField(id); }}
										className="job-form-template__error-summary-link underline underline-offset-2 hover:text-red-900"
									>
										{message}
									</a>
								</li>
							))}
						</ul>
					)}
				</div>
			)}

			<form onSubmit={onSubmit} onChange={onChange} className={['job-form-template', 'space-y-24px max-w-3xl', className].filter(Boolean).join(' ')} data-ref={dataRef}>

				{/* Section 1 — Project Information.
				    Renders for both form types now that Business Line +
				    Sub Type are CMS-managed and shared. The section
				    itself drops Project Name / Description when called
				    with showProjectFields=false (inspection mode). */}
				{projectInformation && (
					<ProjectInformationSection {...projectInformation} />
				)}

				{/* Section 2 — Customer & Location (shared) */}
				<CustomerLocationSection {...customerLocation} />

				{/* Section 3 — Customer Contact (shared) */}
				<OnSiteContactSection {...onSiteContact} />

				{/* Section 4 — Assignment (shared) */}
				<AssignmentSection {...assignment} />

				{/* Section 5 — Inspection Notes (inspection only) */}
				{formType === 'inspection' && inspectionNotes && (
					<InspectionNotesSection {...inspectionNotes} />
				)}

				{/* Section 6 — Billing (shared). Omitted when the caller
				    passes `billing={undefined}` — currently the child-WO
				    create flow under a Project Work Order. */}
				{billing && <BillingSection {...billing} />}

				{/* Section 7 — Settings (shared) */}
				<SettingsSection {...settings} />

				{/* Submit / Cancel — owned by the template so the buttons
				    stay consistent across consumers. */}
				<div className="job-form-template__footer flex items-center gap-12px">
					<button type="submit" disabled={isSubmitting} className="inline-flex items-center justify-center px-24px py-10px text-14px font-sans font-medium bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover min-h-44px transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary">
						{isSubmitting ? (
							<>
								<Spinner size="sm" className="mr-8px" />
								{submittingLabel}
							</>
						) : submitLabel}
					</button>
					<button type="button" onClick={() => window.history.back()} className="inline-flex items-center justify-center px-24px py-10px text-14px font-sans font-medium text-brand-text-muted hover:text-brand-primary min-h-44px transition-colors">
						Cancel
					</button>
				</div>
			</form>
		</>
	);
};

export default JobFormTemplate;
